import React, { useEffect, useRef, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import { createShadowDOMStructure } from "../utils/shadow-dom-helpers";

interface ShadowSheetWrapperProps {
  children: React.ReactNode;
  cssContent: string;
  className?: string;
  sheetClasses?: string;
  sheetStyle?: React.CSSProperties;
  sheetRef?: React.RefObject<HTMLDivElement>;
  onShadowRootReady?: (shadowRoot: ShadowRoot) => void;
  isOpen?: boolean;
}

/**
 * Component that renders children inside a Shadow DOM sheet
 * This provides complete CSS isolation from the parent document
 */
export const ShadowSheetWrapper: React.FC<ShadowSheetWrapperProps> = ({
  children,
  cssContent,
  className = "",
  sheetClasses = "",
  sheetStyle = {},
  sheetRef,
  onShadowRootReady,
  isOpen = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const reactRootRef = useRef<Root | null>(null);
  const sheetContainerRef = useRef<HTMLDivElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (!containerRef.current || shadowRootRef.current) return;

    const { shadowRoot, sheetContainer, contentContainer } =
      createShadowDOMStructure(containerRef.current, cssContent, {
        sheetClasses,
        contentClasses: className,
        sheetStyle,
      });

    shadowRootRef.current = shadowRoot;
    sheetContainerRef.current = sheetContainer;
    contentContainerRef.current = contentContainer;

    const root = createRoot(contentContainer);
    reactRootRef.current = root;

    if (onShadowRootReady) {
      onShadowRootReady(shadowRoot);
    }

    if (sheetRef && "current" in sheetRef) {
      (sheetRef as React.MutableRefObject<HTMLDivElement | null>).current =
        sheetContainer;
    }

    setIsMounted(true);

    return () => {
      if (reactRootRef.current) {
        const rootToUnmount = reactRootRef.current;
        reactRootRef.current = null;
        setTimeout(() => {
          rootToUnmount.unmount();
        }, 0);
      }
    };
  }, [cssContent, onShadowRootReady]);

  useEffect(() => {
    if (contentContainerRef.current) {
      contentContainerRef.current.className = className;
    }
  }, [className]);

  useEffect(() => {
    if (sheetContainerRef.current) {
      sheetContainerRef.current.className = sheetClasses;
      Object.assign(sheetContainerRef.current.style, sheetStyle);
    }
  }, [sheetClasses, sheetStyle]);

  useEffect(() => {
    if (isMounted && reactRootRef.current) {
      reactRootRef.current.render(<>{children}</>);
    }
  }, [isMounted, children]);

  // Handle animation: directly manage the 'sust-sheet--open' class for guaranteed timing
  useEffect(() => {
    if (!isMounted || !sheetContainerRef.current) return;

    if (isOpen) {
      // Ensure the open class is removed first (sheet starts off-screen)
      sheetContainerRef.current.classList.remove("sust-sheet--open");

      // Next frame: add the open class to trigger animation
      requestAnimationFrame(() => {
        if (sheetContainerRef.current) {
          sheetContainerRef.current.classList.add("sust-sheet--open");
        }
      });
    } else {
      // Remove open class when closing
      sheetContainerRef.current.classList.remove("sust-sheet--open");
    }
  }, [isOpen, isMounted]);

  return <div ref={containerRef} style={{ display: "contents" }} />;
};
